import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PopoverContent } from '../../ui/popover';
import { SearchInput } from '../../../shared/components/common/SearchInput';

/**
 * Search-as-you-type filtering for a popover list.
 *
 * The list itself stays the caller's job — it owns the rows and knows which
 * fields are the subject — so this only renders the field and reports what was
 * typed. `debounceMs` is 0 because the RANKING is client-side over rows already
 * loaded and should keep up with the caret; a caller that also issues a request
 * per query (the notifications bell does) debounces that request on its own side,
 * so the visible filter stays instant either way.
 */
interface AppBarPopoverSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

interface AppBarPopoverListProps {
  title: string;
  headerAction?: ReactNode;
  /** Omit to render the popover without a filter field (the default). */
  search?: AppBarPopoverSearch;
  isLoading: boolean;
  isEmpty: boolean;
  emptyIcon: ReactNode;
  emptyMessage: string;
  children: ReactNode;
}

export function AppBarPopoverList({
  title,
  headerAction,
  search,
  isLoading,
  isEmpty,
  emptyIcon,
  emptyMessage,
  children,
}: AppBarPopoverListProps) {
  const { t } = useTranslation('common');

  // Nothing to filter and nothing typed — a search field over "you're all caught
  // up" is noise. Once a query is active the field stays mounted whatever the body
  // is doing: it is the only way back out of a filtered-to-zero list, and
  // unmounting the input the reader is typing into drops the caret with it.
  const showSearch = Boolean(search?.value) || (Boolean(search) && !isLoading && !isEmpty);

  return (
    // Cap to the space Radix measured between the trigger and the viewport
    // edge, so the list never runs off the bottom of the screen. The list's
    // own max-h stays as the design ceiling and shrinks below it when capped.
    <PopoverContent
      align="end"
      className="flex max-h-[var(--radix-popover-content-available-height)] w-80 flex-col p-0"
    >
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-semibold">{title}</span>
        {headerAction}
      </div>

      {search && showSearch && (
        <div className="border-b px-3 py-2">
          <SearchInput
            value={search.value}
            onChange={search.onChange}
            debounceMs={0}
            placeholder={search.placeholder}
            // The shared default is `w-full sm:w-72`, which would stop short of
            // this popover's edge on anything wider than `sm`.
            className="relative w-full sm:w-full"
          />
        </div>
      )}

      <div className="min-h-0 max-h-96 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('app.loading')}
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center gap-2 px-3 py-8 text-center text-muted-foreground">
            {emptyIcon}
            <span className="text-sm">{emptyMessage}</span>
          </div>
        ) : (
          children
        )}
      </div>
    </PopoverContent>
  );
}
